import React from 'react';
import { Link } from 'react-router-dom';
import CommitteeLink from '../committee/CommitteeLink';
import StatCard, { StatCardRow } from '../StatCard';
import { ArrowRight } from 'lucide-react';
import { DETAIL_LINK, DetailCard, DetailLabel, MUTED_TEXT } from '../ballot-initiatives/detail/detailAtoms';
import { financeExplorerHref, financeSummary } from './personDisplay';
import type { CampaignFinanceData } from './types';

interface CampaignFinanceMultiYearProps {
  finance: CampaignFinanceData | null;
  personName: string;
  /** The candidacy's election year: the summary covers that cycle's two calendar years. */
  electionYear?: number | null;
  /** "U.S. Senate campaign" — names what the money is for, when known. */
  campaignLabel?: string | null;
  isPublic?: boolean;
}

function formatMoney(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

/**
 * Campaign finance: three tiles for the cycle (total raised, contributions,
 * average), the committee with its TRACER purpose, and the link into the
 * finance explorer (Pro only — the explorer is an authenticated route).
 */
const CampaignFinanceMultiYear: React.FC<CampaignFinanceMultiYearProps> = ({
  finance,
  personName,
  electionYear,
  campaignLabel,
  isPublic = false,
}) => {
  const summary = finance ? financeSummary(finance, electionYear) : null;
  const explorer = !isPublic && finance ? financeExplorerHref(finance.co_id, personName) : null;

  return (
    <DetailCard id="person-campaign-finance" title="Campaign finance" aside={<span className={MUTED_TEXT}>Source: TRACER</span>}>
      {!finance || !summary ? (
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">No campaign data found</p>
      ) : (
        <>
          <StatCardRow minTileWidth="9rem">
            <StatCard label="Total raised" value={formatMoney(summary.total)} variant="success" />
            <StatCard label="Contributions" value={summary.count.toLocaleString('en-US')} />
            <StatCard label="Avg. contribution" value={formatMoney(summary.average)} variant="info" />
          </StatCardRow>

          <div className="mt-4 border-t border-border-light pt-3 dark:border-border-dark">
            <DetailLabel className={`mb-1 text-[11px] tracking-[0.12em] ${MUTED_TEXT}`}>Committee</DetailLabel>
            <CommitteeLink
              coId={finance.co_id}
              name={finance.committee_name}
              className={`text-sm font-semibold ${DETAIL_LINK}`}
            />
            {finance.committee_purpose?.trim() && (
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-text-secondary dark:text-text-dark-secondary">
                {finance.committee_purpose.trim()}
              </p>
            )}
            <p className={`mt-1 text-xs ${MUTED_TEXT}`}>
              {[campaignLabel, `Contributions dated ${summary.label}`].filter(Boolean).join(' · ')}
            </p>
          </div>

          {explorer && (
            <Link to={explorer} className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${DETAIL_LINK}`}>
              Campaign finance for this {campaignLabel ? 'candidate' : 'person'}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </>
      )}
    </DetailCard>
  );
};

export default CampaignFinanceMultiYear;
